/**
 * Generic storage adapter interface for weavebot
 * This interface should be implemented by specific storage providers
 */

export interface StorageAdapter {
  /**
   * Initialize the storage adapter with configuration
   */
  initialize(config: StorageConfig): Promise<void>;

  /**
   * Create a new record in the specified collection
   */
  create(collection: string, data: any): Promise<StorageRecord>;

  /**
   * Read a record by ID from the specified collection
   */
  read(collection: string, id: string): Promise<StorageRecord | null>;

  /**
   * Update an existing record in the specified collection
   */
  update(collection: string, id: string, data: any): Promise<StorageRecord>;

  /**
   * Delete a record from the specified collection
   */
  delete(collection: string, id: string): Promise<void>;

  /**
   * Query records from a collection with filters
   */
  query(collection: string, filter: StorageFilter): Promise<StorageRecord[]>;

  /**
   * Batch create multiple records
   */
  batchCreate?(collection: string, data: any[]): Promise<StorageRecord[]>;

  /**
   * Get metadata about a collection
   */
  getCollectionMetadata?(collection: string): Promise<CollectionMetadata>;
}

export interface StorageConfig {
  [key: string]: any;
}

export interface StorageRecord {
  id: string;
  data: any;
  metadata?: RecordMetadata;
}

export interface RecordMetadata {
  createdAt?: Date;
  updatedAt?: Date;
  version?: number;
  [key: string]: any;
}

export interface StorageFilter {
  where?: Record<string, any>;
  orderBy?: Array<{
    field: string;
    direction: 'asc' | 'desc';
  }>;
  limit?: number;
  offset?: number;
  include?: string[];
}

export interface CollectionMetadata {
  name: string;
  count?: number;
  fields?: Array<{
    name: string;
    type: string;
    required?: boolean;
  }>;
  indexes?: string[];
}

export class StorageError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: any
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

export interface StorageAdapterFactory {
  create(config: StorageConfig): StorageAdapter;
}